/**
 * SiFly Labor Day promotion — "buy an eFoil set, get a Wing Set on us."
 *
 * Runs 2026-09-03 → 2026-09-08 23:59:59 PT. Funded by SiFly, honored on every
 * dealer storefront (a dealer with no Stripe account can't reach checkout at
 * all, so there is deliberately NO tenant allowlist here — one would be a gate
 * that never fires).
 *
 * The window is enforced against the SERVER clock at charge time, so the offer
 * switches itself off at the cutoff with no deploy. Deleting this module (and
 * its callers) after the promo is pure cleanup, not a deadline.
 *
 * THE TRUST BOUNDARY: the browser sends `isGift` as a HINT, exactly like
 * `payMethod` for the free-ACH shipping perk. `resolveFreeWingSets` is the
 * re-derivation — window, SKU allowlist and per-set allowance are all decided
 * here from data the server fetched, never from a client claim.
 */

/** Shown to the customer so the cart visibly matches SiFly's marketing. */
export const FREE_WING_SET_PROMO_CODE = 'FREEWINGS'

// 2026-09-03 00:00:00 PT. September is PDT (UTC−7), confirmed Timm 2026-09-04.
export const FREE_WING_SET_PROMO_START = '2026-09-03T07:00:00.000Z'
// 2026-09-08 23:59:59.999 PT — the last instant of Tuesday.
export const FREE_WING_SET_PROMO_END = '2026-09-09T06:59:59.999Z'

/**
 * The six SiFly Wing Set SKUs a customer may choose from. Five are $840 MSRP;
 * Dual Ride 1000 is $390. All six qualify — SiFly's copy says "choose one bonus
 * SiFly Wing Set" without restricting value (confirmed Timm 2026-09-04).
 *
 * SKU ids, not product ids: the checkout resolver and the webhook both key on
 * `skus.id`, and every one of these products has exactly one active SKU.
 */
export const FREE_WING_SET_SKU_IDS: readonly string[] = [
  '3fa9d269-8d4a-4c10-a4d0-baba6f4cf88c', // Sport 700 + SP240
  'ba9fdcb8-2cf5-4d65-a07f-6a9405704f08', // Glide 900 + SP240
  '520e32f4-f92b-49db-904c-a6b9a7aaa42a', // Glide 1100 + SP240
  '5f756f94-b3d4-424b-ba10-2bbc1ebf4d8a', // Cruiser 1300 + CR300
  '977682c8-8202-497e-b89a-21397fe22616', // Cruiser 1900 + CR340
  '33b5641a-2cf4-4f2a-b371-db2d849de9c4', // Dual Ride 1000 + FRP340 + FRP240
]

/** The shape both the cart drawer and the checkout resolver can supply. */
export type PromoCartItem = {
  productType: string | null | undefined
  source: string | null | undefined
  skuId?: string | null
  quantity: number
  /** Client HINT that this line is the free Wing Set. Never trusted directly. */
  isGift?: boolean
}

export type FreeWingSetVerdict = {
  /** Per-index: true = an honored free Wing Set, charge $0. */
  granted: boolean[]
  /** How many free Wing Sets this cart has earned. */
  allowance: number
  /** How many lines claimed to be one. */
  claimed: number
  /** Indexes that claimed a gift and were DENIED — the caller must not charge $0. */
  deniedIndexes: number[]
}

export function promoWindowOpen(now: Date = new Date()): boolean {
  const t = now.getTime()
  return t >= Date.parse(FREE_WING_SET_PROMO_START) && t <= Date.parse(FREE_WING_SET_PROMO_END)
}

export function isFreeWingSetSku(skuId: string | null | undefined): boolean {
  return !!skuId && FREE_WING_SET_SKU_IDS.includes(skuId)
}

/**
 * A qualifying purchase is a SiFly eFoil set — every active SiFly bundle is a
 * Rider / Stellar / Lite set, which is exactly the three series the T&C names,
 * so bundle-ness IS the eligibility test and there is no product-id list to
 * keep in step with the catalog.
 *
 * A dealer-built bundle (`source: 'dealer'`) is the dealer's own kit, not a
 * SiFly eFoil set, and does not earn a bonus SiFly funds.
 */
export function qualifyingSetUnits(items: readonly PromoCartItem[]): number {
  return items.reduce(
    (sum, i) =>
      i.productType === 'bundle' && i.source === 'sifly' && !i.isGift
        ? sum + Math.max(0, i.quantity)
        : sum,
    0,
  )
}

/**
 * Decide which claimed gift lines are honored. "Limit one (1) bonus Wing Set
 * per eFoil set purchased" — so allowance is the number of qualifying SET
 * UNITS, and gifts beyond it are denied rather than silently discounted.
 *
 * A gift line must be quantity 1: the UI locks it, and a tampered quantity is a
 * denial rather than a multiplied giveaway.
 */
export function resolveFreeWingSets(
  items: readonly PromoCartItem[],
  now: Date = new Date(),
): FreeWingSetVerdict {
  const allowance = qualifyingSetUnits(items)
  const open = promoWindowOpen(now)
  const granted: boolean[] = items.map(() => false)
  const deniedIndexes: number[] = []
  let claimed = 0
  let used = 0

  items.forEach((item, i) => {
    if (!item.isGift) return
    claimed++
    const ok =
      open &&
      item.productType === 'simple' &&
      item.source === 'sifly' &&
      item.quantity === 1 &&
      isFreeWingSetSku(item.skuId) &&
      used < allowance
    if (ok) {
      granted[i] = true
      used++
    } else {
      deniedIndexes.push(i)
    }
  })

  return { granted, allowance, claimed, deniedIndexes }
}

/**
 * Stripe line label for a gifted Wing Set. Uses ` · ` rather than an em dash
 * because the name it wraps already contains one (`Glide 900 Wing Set — Glide
 * 900 + SP240`), and three dashes in a row reads as a typo on the Stripe page.
 */
export function freeWingSetLineLabel(name: string): string {
  return `${name} · FREE with your eFoil set (${FREE_WING_SET_PROMO_CODE})`
}
